<template>
    <div
        class="o-steps-panel steps-panel"
        :class="{'steps-panel--active': isActive}"
    >
        <button
            type="button"
            role="button"
            class="steps-panel__control"
            :aria-label="ariaLabel"
            @click="changeState"
        >
            <svg-icon
                v-if="isActive"
                original
                name="icons/eye-cross"
                class="steps-panel__eye-icon"
            />
            <svg-icon
                v-if="!isActive"
                original
                name="icons/eye"
                class="steps-panel__eye-icon"
            />
            <svg-icon
                original
                name="icons/arrow-up"
                class="steps-panel__arrow-icon"
                :class="{'steps-panel__arrow-icon--down': isActive}"
            />
        </button>
        <div 
            v-if="!active"
            class="steps-panel__container steps-panel__container--secondary"
            @click="changeState"
        >
            <h4 class="steps-panel__container-heading">
                {{ $t('o-steps-panel-tip') }}
            </h4>
        </div>
        <div class="steps-panel__container steps-panel__container--primary">
            <div
                v-if="overlay"
                class="steps-panel__overlay"
            >
                <AButton
                    :text="$t('ui.center-road-directions')"
                    @onClick="focusOnStep"
                >
                    <template #left>
                        <span class="steps-panel__overlay-icon-wrapper">
                            <svg-icon
                                original
                                name="icons/arrow-up-2"
                            />
                        </span>
                    </template>
                </AButton>
            </div>
            <ABackButton
                :text="$t('ui.close')"
                :class="{'m-back-button--highlight': !overlay}"
                :tabindex="tabIndex"
                @onClick="handleCloseClick"
            />
            <AButtonSwitcher
                class="steps-panel__button-switcher"
                :text="$t('ui.previous')"
                mode="left"
                :disabled="activeStepIndex === 0"
                :tabindex="tabIndex"
                @onClick="handlePrevStep"
            />
            <div
                v-if="activeStep"
                class="steps-panel__wrapper"
            >
                <div class="steps-panel__index">
                    {{ activeStepIndex + 1}}
                </div>
                <svg-icon
                    v-if="activeStepIndex === path.length - 1"
                    original
                    class="steps-panel__icon"
                    name="map-navigation/finish"
                />
                <svg-icon
                    v-else
                    original
                    class="steps-panel__icon"
                    :name="`map-navigation/${activeStep.direction}`"
                />
                <span class="steps-panel__text">
                    {{ t(activeStep, 'distance') }} {{ $t('ui.meter') }}
                </span>
            </div>
            <AButtonSwitcher
                class="steps-panel__button-switcher"
                :text="$t('ui.next')"
                :disabled="activeStepIndex === path.length - 1"
                :tabindex="tabIndex"
                @onClick="handleNextStep"
            />
        </div>
    </div>
</template>

<script>
import {
    mapState,
    mapGetters
} from 'vuex';

import AButton from '../../atoms/a-button/a-button';
import ABackButton from '../../atoms/a-m-back-button/a-m-back-button';
import AButtonSwitcher from '../../atoms/a-button-switcher/a-button-switcher';

import {
    MAP_SHOW_PATH_STEPS,
    UI_STEP_SELECTED,
    UI_MAP_CLICKED
} from "../../../shared/components/map-viewer/constants/map-events";

import { DROP_OVERLAY } from '../../../utils/constants/bus-events-constants';
    
export default {
    name: 'OStepsPanel',
    components: {
        AButton,
        ABackButton,
        AButtonSwitcher
    },
    props: {
        ariaLabel: {
            type: String,
            default: 'Step panel control'
        }
    },
    data() {
        return {
            overlay: false,
            active: false
        }
    },
    computed: {
        ...mapState({
            path: state => state.activePath?.steps,
            activeStep: state => state.activeStep,
            activeStepIndex: state => state.activeStepIndex
        }),
        ...mapGetters([
            't'
        ]),
        isActive() {
            return this.active
        },
        tabIndex() {
            return this.active ? 0 : -1
        }
    },
    mounted() {
        this.checkActiveStep();
        this.initEventsListeners();
        this.alignButtons();
    },
    updated() {
        this.alignButtons();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
    methods: {
        initEventsListeners() {
            this.$bus.$on(UI_MAP_CLICKED, this.showOverlay);
            this.$bus.$on(DROP_OVERLAY, this.removeOverlay);
            this.$bus.$on(UI_STEP_SELECTED, this.removeOverlay);
        },
        deactivateEventsListeners() {
            this.$bus.$off(UI_MAP_CLICKED, this.showOverlay);
            this.$bus.$off(DROP_OVERLAY, this.removeOverlay);
            this.$bus.$off(UI_STEP_SELECTED, this.removeOverlay);
        },
        checkActiveStep() {
            if (this.activeStepIndex === null) {
                this.$store.commit('setActiveStepIndex', 0);
                this.$store.commit('setActiveStep', this.path[0]);
                this.$bus.$emit(MAP_SHOW_PATH_STEPS, 0);
            }
        },
        handleNextStep() {
            if (this.activeStepIndex === this.path.length) return;
            this.$store.commit('setActiveStepIndex', this.activeStepIndex + 1);
            this.$store.commit('setActiveStep', this.path[this.activeStepIndex]);
            this.$bus.$emit(MAP_SHOW_PATH_STEPS, this.activeStepIndex);
        },
        handlePrevStep() {
            if (this.activeStepIndex === 0) return;
            this.$store.commit('setActiveStepIndex', this.activeStepIndex - 1);
            this.$store.commit('setActiveStep', this.path[this.activeStepIndex]);
            this.$bus.$emit(MAP_SHOW_PATH_STEPS, this.activeStepIndex);
        },
        handleCloseClick() {
            this.$emit('onClose', true);
        },
        focusOnStep() {
            this.$bus.$emit(MAP_SHOW_PATH_STEPS, this.activeStepIndex);
            this.overlay = false;
        },
        showOverlay() {
            this.overlay = true;
        },
        removeOverlay() {
            this.overlay = false;
        },
        alignButtons() {
            const arr = [...document.getElementsByClassName('button-switcher')];
            const values = [];

            arr.forEach(item => {
                values.push(parseInt(item.offsetWidth));
            });

            arr.forEach(item => {
                item.style.width = Math.max(...values) + 'px';
            });
        },
        changeState() {
            this.active = !this.active
        }
    }
}
</script>

<style lang="scss">
@import "o-steps-panel";   
</style>
